import { BaseEntity } from '@processpuzzle/base-entity';

export enum BillingInterval {
  MONTHLY = 'MONTHLY',
  YEARLY = 'YEARLY',
}

export enum SubscriptionStatus {
  TRIALING = 'TRIALING',
  ACTIVE = 'ACTIVE',
  PAST_DUE = 'PAST_DUE',
  CANCELED = 'CANCELED',
}

export enum InvoiceStatus {
  DRAFT = 'DRAFT',
  ISSUED = 'ISSUED',
  PAID = 'PAID',
  VOID = 'VOID',
}

export enum UsageMetric {
  USERS = 'USERS',
  ENTITY_OBJECTS = 'ENTITY_OBJECTS',
  DOCUMENTS = 'DOCUMENTS',
  WORKFLOW_INSTANCES = 'WORKFLOW_INSTANCES',
  STORAGE_BYTES = 'STORAGE_BYTES',
  API_CALLS = 'API_CALLS',
}

/**
 * Money as an integer of the currency's minor unit — cents — and never as a `number` of major units.
 *
 * JavaScript has one numeric type and it is a binary float, so `49.00` is not exactly representable
 * and a column of them does not sum to what a customer was charged. Keeping the integer the API sends
 * and formatting it for display is the only arrangement in which the figure on screen is the figure
 * on the invoice. {@link formatMinor} is the one place that division happens.
 */
export function formatMinor(amountMinor: number | undefined, currency: string | undefined): string {
  if (amountMinor === undefined || amountMinor === null) return '';
  const major = amountMinor / 100;
  return new Intl.NumberFormat(undefined, { style: 'currency', currency: currency || 'EUR' }).format(major);
}

/** A per-metric ceiling on a plan. An absent metric means unmetered; `0` means unavailable. */
export interface PlanLimit {
  metric: UsageMetric;
  maxQuantity: number;
}

/** A purchasable entitlement level. Keyed by `code`, which a subscription names. */
export class Plan implements BaseEntity {
  readonly id: string;
  readonly code: string;
  name: string;
  description: string | undefined;
  interval: BillingInterval;
  currency: string;
  amountMinor: number;
  limits: PlanLimit[];

  constructor(code?: string, name?: string, description?: string, interval?: BillingInterval, currency?: string, amountMinor?: number, limits?: PlanLimit[]) {
    this.code = code ?? '';
    this.id = this.code;
    this.name = name ?? '';
    this.description = description;
    this.interval = interval ?? BillingInterval.MONTHLY;
    this.currency = currency ?? 'EUR';
    this.amountMinor = amountMinor ?? 0;
    this.limits = limits ?? [];
  }

  /** The price, formatted for display. See {@link formatMinor} on why the raw value stays an integer. */
  get price(): string {
    return formatMinor(this.amountMinor, this.currency);
  }
}

/**
 * What a tenant is currently entitled to, and for which period.
 *
 * `quantity`, `trialEndsAt` and `cancelAtPeriodEnd` were missing from this class while the contract
 * carried the first two all along — so the staff screen showed a subscription without saying how
 * many seats it was for. They are here because the customer portal needs all three, and adding them
 * on one side only would have been two models of one shape.
 *
 * `cancelAtPeriodEnd` is why {@link isCancelling} exists rather than the template asking twice.
 */
export class Subscription implements BaseEntity {
  readonly id: string;
  orgKey: string;
  planCode: string;
  status: SubscriptionStatus;
  quantity: number;
  cancelAtPeriodEnd: boolean;
  currentPeriodStart: string | undefined;
  currentPeriodEnd: string | undefined;
  trialEndsAt: string | undefined;
  canceledAt: string | undefined;
  createdAt: string | undefined;
  updatedAt: string | undefined;

  constructor(properties: SubscriptionProperties = {}) {
    this.id = properties.id ?? '';
    this.orgKey = properties.orgKey ?? '';
    this.planCode = properties.planCode ?? '';
    this.status = properties.status ?? SubscriptionStatus.TRIALING;
    // One seat, matching the backend's own default for a subscription created without a quantity.
    this.quantity = properties.quantity ?? 1;
    this.cancelAtPeriodEnd = properties.cancelAtPeriodEnd ?? false;
    this.currentPeriodStart = properties.currentPeriodStart;
    this.currentPeriodEnd = properties.currentPeriodEnd;
    this.trialEndsAt = properties.trialEndsAt;
    this.canceledAt = properties.canceledAt;
    this.createdAt = properties.createdAt;
    this.updatedAt = properties.updatedAt;
  }

  /**
   * Cancelled, but still running until {@link currentPeriodEnd}.
   *
   * A distinct state from `status === CANCELED`, and the distinction is the whole point: the customer
   * has paid for the running period and is owed it, so service continues and the status does not
   * change until the period elapses. A screen that read only the status would tell a customer who
   * just cancelled that nothing had happened.
   */
  get isCancelling(): boolean {
    return this.cancelAtPeriodEnd && this.status !== SubscriptionStatus.CANCELED;
  }

  /** Whether this subscription is in a trial that has not ended. */
  get isTrialing(): boolean {
    return this.status === SubscriptionStatus.TRIALING;
  }
}

export interface SubscriptionProperties {
  id?: string;
  orgKey?: string;
  planCode?: string;
  status?: SubscriptionStatus;
  quantity?: number;
  cancelAtPeriodEnd?: boolean;
  currentPeriodStart?: string;
  currentPeriodEnd?: string;
  trialEndsAt?: string;
  canceledAt?: string;
  createdAt?: string;
  updatedAt?: string;
}

/** One metric's measured quantity for one tenant over one period. */
export interface UsageRecord {
  id: string;
  orgKey: string;
  metric: UsageMetric;
  quantity: number;
  periodStart?: string;
  periodEnd?: string;
  recordedAt?: string;
}

/** One charge on an invoice. `metric` is absent for a flat plan fee. */
export interface InvoiceLine {
  description: string;
  metric?: UsageMetric;
  quantity: number;
  unitAmountMinor: number;
  amountMinor: number;
}

/** What a tenant was charged for one period. A `DRAFT` has no number: numbers must be gapless. */
export class Invoice implements BaseEntity {
  readonly id: string;
  orgKey: string;
  number: string | undefined;
  status: InvoiceStatus;
  currency: string;
  totalMinor: number;
  periodStart: string | undefined;
  periodEnd: string | undefined;
  issuedAt: string | undefined;
  paidAt: string | undefined;
  lines: InvoiceLine[];

  constructor(properties: InvoiceProperties = {}) {
    this.id = properties.id ?? '';
    this.orgKey = properties.orgKey ?? '';
    this.number = properties.number;
    this.status = properties.status ?? InvoiceStatus.DRAFT;
    this.currency = properties.currency ?? 'EUR';
    this.totalMinor = properties.totalMinor ?? 0;
    this.periodStart = properties.periodStart;
    this.periodEnd = properties.periodEnd;
    this.issuedAt = properties.issuedAt;
    this.paidAt = properties.paidAt;
    this.lines = properties.lines ?? [];
  }

  get total(): string {
    return formatMinor(this.totalMinor, this.currency);
  }
}

export interface InvoiceProperties {
  id?: string;
  orgKey?: string;
  number?: string;
  status?: InvoiceStatus;
  currency?: string;
  totalMinor?: number;
  periodStart?: string;
  periodEnd?: string;
  issuedAt?: string;
  paidAt?: string;
  lines?: InvoiceLine[];
}

/**
 * Everything the billing screen shows for one tenant, in one response.
 *
 * `subscription` and `plan` are independently optional, and both being absent is the common case
 * today rather than an error: nothing in the platform creates subscriptions yet. A subscription can
 * also name a plan withdrawn from the catalog, which is why the plan is optional even when the
 * subscription is present.
 */
export interface OrganizationBilling {
  orgKey: string;
  subscription?: Subscription;
  plan?: Plan;
  /**
   * Who is invoiced. Independently optional from the subscription: the invoiced party outlives it by
   * ten years, so it can be present long after the subscription that justified it has gone — and a
   * tenant bootstrapped rather than bought has neither.
   *
   * Typed loosely here on purpose. The staff screen does not render it; it is on the projection
   * because one read serves both that screen and the customer portal, and the portal has its own
   * `BillingParty` model with the form validation that belongs to editing it.
   */
  billingParty?: unknown;
  usage: UsageRecord[];
  invoices: Invoice[];
}
